import React, { useState, useEffect, useCallback } from "react";
import { onedriveAPI } from "../lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  Folder,
  FolderOpen,
  ChevronRight,
  ArrowLeft,
  Loader2,
  Plus,
  Cloud,
  Check,
} from "lucide-react";

export function OneDriveFolderPicker({ open, onClose, onSelect }) {
  const [currentPath, setCurrentPath] = useState("");
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [newFolderName, setNewFolderName] = useState("");
  const [creating, setCreating] = useState(false);
  const [showNewFolder, setShowNewFolder] = useState(false);

  const breadcrumbs = currentPath
    ? ["OneDrive", ...currentPath.split("/")]
    : ["OneDrive"];

  const loadFolders = useCallback(async (path) => {
    setLoading(true);
    setError(null);
    try {
      const res = await onedriveAPI.listFolders(path);
      setFolders(res.data.folders);
    } catch (err) {
      setError(err.response?.data?.detail || "Errore nel caricamento cartelle");
      setFolders([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      setCurrentPath("");
      loadFolders("");
    }
  }, [open, loadFolders]);

  const navigateTo = (folderName) => {
    const newPath = currentPath ? `${currentPath}/${folderName}` : folderName;
    setCurrentPath(newPath);
    loadFolders(newPath);
    setShowNewFolder(false);
  };

  const goBack = () => {
    const parts = currentPath.split("/");
    parts.pop();
    const newPath = parts.join("/");
    setCurrentPath(newPath);
    loadFolders(newPath);
    setShowNewFolder(false);
  };

  const goToBreadcrumb = (index) => {
    if (index === 0) {
      setCurrentPath("");
      loadFolders("");
    } else {
      const parts = currentPath.split("/");
      const newPath = parts.slice(0, index).join("/");
      setCurrentPath(newPath);
      loadFolders(newPath);
    }
    setShowNewFolder(false);
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    setCreating(true);
    try {
      await onedriveAPI.createFolder(currentPath, newFolderName.trim());
      setNewFolderName("");
      setShowNewFolder(false);
      loadFolders(currentPath);
    } catch (err) {
      setError(err.response?.data?.detail || "Errore nella creazione cartella");
    } finally {
      setCreating(false);
    }
  };

  const handleSelect = () => {
    onSelect(currentPath);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-foreground">
            <Cloud size={20} className="text-[#2D7BB7]" />
            Scegli Cartella OneDrive
          </DialogTitle>
        </DialogHeader>

        {/* Breadcrumb */}
        <div className="flex items-center gap-1 text-sm flex-wrap px-1" data-testid="folder-breadcrumb">
          {breadcrumbs.map((crumb, i) => (
            <React.Fragment key={i}>
              {i > 0 && <ChevronRight size={14} className="text-muted-foreground" />}
              <button
                onClick={() => goToBreadcrumb(i)}
                className={`hover:text-[#2D7BB7] transition-colors px-1 py-0.5 rounded ${
                  i === breadcrumbs.length - 1
                    ? "text-[#2D7BB7] font-medium"
                    : "text-muted-foreground"
                }`}
              >
                {crumb}
              </button>
            </React.Fragment>
          ))}
        </div>

        {/* Folder list */}
        <div className="flex-1 min-h-[250px] max-h-[350px] overflow-y-auto border border-border rounded-lg">
          {loading ? (
            <div className="flex items-center justify-center h-full py-12">
              <Loader2 className="h-6 w-6 animate-spin text-[#2D7BB7]" />
            </div>
          ) : error ? (
            <div className="p-4 text-center text-red-600 text-sm">{error}</div>
          ) : (
            <div className="divide-y divide-border">
              {currentPath && (
                <button
                  onClick={goBack}
                  className="w-full flex items-center gap-3 px-4 py-3 hover:bg-accent/50 transition-colors text-left"
                  data-testid="folder-go-back"
                >
                  <ArrowLeft size={18} className="text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">..</span>
                </button>
              )}
              {folders.length === 0 && !currentPath && (
                <div className="p-6 text-center text-muted-foreground text-sm">
                  Nessuna cartella trovata
                </div>
              )}
              {folders.map((folder) => (
                <button
                  key={folder.id}
                  onClick={() => navigateTo(folder.name)}
                  className="w-full flex items-center gap-3 px-4 py-3 hover:bg-accent/50 transition-colors text-left group"
                  data-testid={`folder-item-${folder.name}`}
                >
                  <Folder size={18} className="text-amber-500 group-hover:hidden" />
                  <FolderOpen size={18} className="text-amber-500 hidden group-hover:block" />
                  <div className="flex-1 min-w-0">
                    <span className="text-sm font-medium text-foreground truncate block">
                      {folder.name}
                    </span>
                    {folder.child_count > 0 && (
                      <span className="text-xs text-muted-foreground">
                        {folder.child_count} elementi
                      </span>
                    )}
                  </div>
                  <ChevronRight size={16} className="text-muted-foreground opacity-0 group-hover:opacity-100" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* New Folder */}
        {showNewFolder ? (
          <div className="flex items-center gap-2">
            <Input
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              placeholder="Nome nuova cartella"
              className="flex-1"
              onKeyDown={(e) => e.key === "Enter" && handleCreateFolder()}
              autoFocus
              data-testid="new-folder-input"
            />
            <Button
              size="sm"
              onClick={handleCreateFolder}
              disabled={creating || !newFolderName.trim()}
              className="bg-[#2D7BB7] hover:bg-[#5BA4D4] text-white"
              data-testid="new-folder-confirm"
            >
              {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check size={16} />}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => { setShowNewFolder(false); setNewFolderName(""); }}
            >
              Annulla
            </Button>
          </div>
        ) : (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowNewFolder(true)}
            className="w-fit"
            data-testid="new-folder-btn"
          >
            <Plus size={16} className="mr-1" />
            Nuova Cartella
          </Button>
        )}

        {/* Current selection info */}
        <div className="px-3 py-2 bg-blue-50 rounded-lg text-sm text-blue-800 border border-blue-200">
          Salvataggio in: <strong>{currentPath || "Root di OneDrive"}</strong>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Annulla
          </Button>
          <Button
            onClick={handleSelect}
            className="bg-[#2D7BB7] hover:bg-[#5BA4D4] text-white"
            data-testid="folder-select-btn"
          >
            <Check size={16} className="mr-2" />
            Seleziona Cartella
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
